export const languages = ["en", "ja", "fr", "de", "pt", "es", "ar", "it", "zh-CN", "zh-TW", "ko"] as const;
export type Language = typeof languages[number];
export const languageNames: Record<Language, string> = {
  en: "English", ja: "日本語", fr: "Français", de: "Deutsch",
  pt: "Português", es: "Español", ar: "العربية", it: "Italiano",
  "zh-CN": "简体中文", "zh-TW": "繁體中文", ko: "한국어",
};
export function isLanguage(value: unknown): value is Language {
  return languages.some((language) => language === value);
}
export function localeFor(language: Language) {
  return { en: "en-US", ja: "ja-JP", fr: "fr-FR", de: "de-DE", pt: "pt-BR", es: "es-ES", ar: "ar-SA", it: "it-IT", "zh-CN": "zh-CN", "zh-TW": "zh-TW", ko: "ko-KR" }[language];
}
export function directionFor(language: Language) {
  return language === "ar" ? "rtl" : "ltr";
}

export function matchLanguage(tag: string): Language | undefined {
  try {
    const locale = new Intl.Locale(tag.trim());
    if (locale.language === "zh") {
      if (locale.script === "Hant") return "zh-TW";
      if (locale.script === "Hans") return "zh-CN";
      return ["TW", "HK", "MO"].includes(locale.region ?? "") ? "zh-TW" : "zh-CN";
    }
    return isLanguage(locale.language) ? locale.language : undefined;
  } catch {
    return undefined;
  }
}

export function preferredLanguage(preferences: readonly string[]): Language {
  for (const preference of preferences) {
    const match = matchLanguage(preference);
    if (match) return match;
  }
  return "en";
}

export function acceptLanguagePreferences(header: string | null): string[] {
  return (header ?? "").split(",").map((part) => {
    const [tag, ...parameters] = part.trim().split(";");
    const quality = parameters.find((parameter) => parameter.trim().startsWith("q="));
    const weight = quality === undefined ? 1 : Number(quality.trim().slice(2));
    return { tag, weight };
  }).filter(({ tag, weight }) => tag && Number.isFinite(weight) && weight > 0 && weight <= 1)
    .sort((a, b) => b.weight - a.weight).map(({ tag }) => tag);
}

export const languageCookie = "agentbuck_language";

export function languageFromCookie(cookie: string | null): Language | undefined {
  const value = (cookie ?? "").split(";").map((part) => part.trim())
    .find((part) => part.startsWith(languageCookie + "="))?.slice(languageCookie.length + 1);
  return isLanguage(value) ? value : undefined;
}

export function requestLanguage(url: URL, headers: Headers): Language {
  const query = url.searchParams.get("lang");
  return isLanguage(query) ? query : languageFromCookie(headers.get("cookie"))
    ?? preferredLanguage(acceptLanguagePreferences(headers.get("accept-language")));
}
